import _ from 'lodash';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { MODES_REGLEMENTS } from '@/constants';
import { ModeReglementContext } from '@/contexts';
import { CACHE_TTL, isCacheValid } from '@/utils/cache';
import { useRegieStore } from '@/zustand';
import useRecuStatsStore from '@/zustand/accueil/useRecuStatsStore';

/**
 * Hook pour récupérer les statistiques des reçus du jour avec cache automatique
 * Dépend du ModeReglementContext pour déterminer les modes actifs
 * Vérifie le cache à chaque utilisation et fetch si nécessaire
 */
const useRecuStatsData = () => {
  const regieId = useRegieStore((state) => state.regie?.identifiant);
  const { regieModesReglement } = useContext(ModeReglementContext);

  const recuStats = useRecuStatsStore((state) => state.data);
  const loading = useRecuStatsStore((state) => state.loading);
  const error = useRecuStatsStore((state) => state.error);
  const cache = useRecuStatsStore((state) => state.cache);
  const fetch = useRecuStatsStore((state) => state.fetch);

  // Calcul des flags pour les modes de règlement internet
  const hasCBI = useMemo(
    () => _.some(regieModesReglement, { code: MODES_REGLEMENTS.CARTE_BANCAIRE_INTERNET }),
    [regieModesReglement],
  );

  const hasPRI = useMemo(
    () => _.some(regieModesReglement, { code: MODES_REGLEMENTS.PRELEVEMENT_INTERNET }),
    [regieModesReglement],
  );

  // Vérification du cache à chaque utilisation du hook
  useEffect(() => {
    // Attendre que les modes de règlement soient chargés
    if (!regieId || _.isEmpty(regieModesReglement)) return;

    if (!isCacheValid(cache.timestamp, CACHE_TTL.RECU_STATS, cache.regieId, regieId)) {
      fetch(regieId, hasCBI, hasPRI);
    }
  }, [regieId, regieModesReglement, hasCBI, hasPRI, cache.timestamp, cache.regieId, fetch]);

  // Fonction pour forcer un refresh manuel
  const refresh = useCallback(() => {
    // Attendre que les modes de règlement soient chargés
    if (regieId && !_.isEmpty(regieModesReglement)) {
      fetch(regieId, hasCBI, hasPRI, true);
    }
  }, [regieId, regieModesReglement, hasCBI, hasPRI, fetch]);

  return {
    recuStats,
    loading,
    error,
    refresh,
  };
};

export default useRecuStatsData;
